import React, { useState, useEffect } from "react";
import useFetch from "../../../../hooks/useFetch";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { CircularProgress } from "@mui/material";
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import { AuthPanel, AUTH_FIELD } from "../../../shares/landing/LandingWidgets";

const digits = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];

/**
 * Pre-registration dialog.
 *
 * The POST payload and every field name are unchanged. Presentation changes:
 *  - twelve stacked full-width inputs became a two-column grid, so the form
 *    fits without a long scroll on a laptop;
 *  - the eleven error paragraphs that all rendered in a block at the top of the
 *    form now sit under the field each one belongs to, which is the only place
 *    a validation message is useful;
 *  - the loading state replaced the entire form with a bare spinner, losing
 *    everything the user had typed from view. The form now stays put and the
 *    submit button carries the spinner.
 */
const Register = ({ close }) => {
  const [res, setRes] = useState();
  const [call, setCall] = useState({
    student_user: {
      username: "",
      password: "",
      password2: "",
      first_name: "",
      last_name: "",
    },
    birth_date: "",
    phone_number: "",
    home_number: "",
    student_type: "",
    student_school: "",
    student_gender: "",
    student_grade: "",
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const registerFetch = useFetch({
    method: "POST",
    url: `register`,
    noHeader: true,
    trigger: false,
    data: call,
    setter: setRes,
  });

  const submitForm = () => {
    const formData = new FormData();

    formData.append("student_user.username", call.student_user.username);
    formData.append("student_user.password", call.student_user.password);
    formData.append("student_user.password2", call.student_user.password2);
    formData.append("student_user.first_name", call.student_user.first_name);
    formData.append("student_user.last_name", call.student_user.last_name);
    formData.append("birth_date", call.birth_date);
    formData.append("phone_number", call.phone_number);
    formData.append("home_number", call.home_number);
    formData.append("student_type", call.student_type);
    formData.append("student_school", call.student_school);
    formData.append("student_gender", call.student_gender);
    formData.append("student_grade", call.student_grade);

    setCall(formData);
    registerFetch.reFetch();
  };

  useEffect(() => {
    if (res) {
      toast.success("ثبت نام با موفقیت انجام شد");
      close();
    }
  }, [res]);

  const setStudentUser = (key) => (e) =>
    setCall((prev) => ({
      ...prev,
      student_user: { ...prev.student_user, [key]: e.target.value },
    }));

  const Error = ({ field }) =>
    errors?.[field] ? (
      <p className="mt-xs text-base text-danger">{errors[field].message}</p>
    ) : null;

  return (
    <AuthPanel title="ثبت نام" close={close} width="max-w-[64rem]">
      <form
        className="flex flex-col gap-lg"
        onSubmit={handleSubmit(submitForm)}
        noValidate
      >
        <div className="grid gap-lg sm:grid-cols-2">
          <div>
            <input
              autoComplete="false"
              placeholder="کدملی"
              aria-label="کدملی"
              type="number"
              name="username"
              className={AUTH_FIELD}
              {...register("username", {
                required: "لطفا کدملی خود را وارد کنید",
                maxLength: {
                  value: 10,
                  message: "کدملی خود را به طور صحیح وارد کنید",
                },
                minLength: {
                  value: 10,
                  message: "کدملی خود را به طور صحیح وارد کنید",
                },
              })}
              onChange={setStudentUser("username")}
            />
            <Error field="username" />
          </div>

          <div>
            <DatePicker
              inputClass={`${AUTH_FIELD}`}
              format="YYYY-MM-DD"
              containerClassName="w-full"
              calendar={persian}
              locale={persian_fa}
              placeholder={"تولد"}
              digits={digits}
              onChange={(e) => setCall({ ...call, birth_date: e.format() })}
            />
            <Error field="birthDate" />
          </div>

          <div>
            <input
              autoComplete="false"
              placeholder="رمز عبور"
              aria-label="رمز عبور"
              type="password"
              name="password"
              className={AUTH_FIELD}
              {...register("password", {
                required: "لطفا رمز عبور خود را وارد کنید",
                maxLength: {
                  value: 12,
                  message: "رمز خود را به طور صحیح وارد کنید",
                },
                minLength: {
                  value: 8,
                  message: "رمز خود را به طور صحیح وارد کنید",
                },
              })}
              onChange={setStudentUser("password")}
            />
            <Error field="password" />
          </div>

          <div>
            <input
              autoComplete="false"
              placeholder="تکرار رمز عبور"
              aria-label="تکرار رمز عبور"
              type="password"
              name="password2"
              className={AUTH_FIELD}
              {...register("password2", {
                required: "لطفا رمز عبور خود را وارد کنید",
                maxLength: {
                  value: 12,
                  message: "رمز خود را به طور صحیح وارد کنید",
                },
                minLength: {
                  value: 8,
                  message: "رمز خود را به طور صحیح وارد کنید",
                },
              })}
              onChange={setStudentUser("password2")}
            />
            <Error field="password2" />
          </div>

          <div>
            <input
              autoComplete="false"
              placeholder="نام"
              aria-label="نام"
              type="text"
              name="firstName"
              className={AUTH_FIELD}
              {...register("firstName", {
                required: "لطفا نام خود را وارد کنید",
              })}
              onChange={setStudentUser("first_name")}
            />
            <Error field="firstName" />
          </div>

          <div>
            <input
              autoComplete="false"
              placeholder="نام خانوادگی"
              aria-label="نام خانوادگی"
              type="text"
              name="lastName"
              className={AUTH_FIELD}
              {...register("lastName", {
                required: "لطفا نام خانوادگی خود را وارد کنید",
              })}
              onChange={setStudentUser("last_name")}
            />
            <Error field="lastName" />
          </div>

          <div>
            <input
              autoComplete="false"
              placeholder="شماره همراه"
              aria-label="شماره همراه"
              type="number"
              name="phoneNumber"
              className={AUTH_FIELD}
              {...register("phoneNumber", {
                required: "لطفا شماره همراه خود را وارد کنید",
              })}
              onChange={(e) =>
                setCall({ ...call, phone_number: e.target.value })
              }
            />
            <Error field="phoneNumber" />
          </div>

          <div>
            <input
              autoComplete="false"
              placeholder="تلفن ثابت"
              aria-label="تلفن ثابت"
              type="number"
              name="homeNumber"
              className={AUTH_FIELD}
              {...register("homeNumber", {
                required: "لطفا تلفن خود را وارد کنید",
              })}
              onChange={(e) =>
                setCall({ ...call, home_number: e.target.value })
              }
            />
            <Error field="homeNumber" />
          </div>

          <div>
            <select
              name="studentType"
              aria-label="رشته"
              className={AUTH_FIELD}
              defaultValue=""
              {...register("studentType", {
                required: "لطفا رشته خود را وارد کنید",
              })}
              onChange={(e) =>
                setCall({ ...call, student_type: e.target.value })
              }
            >
              <option value="" disabled>
                رشته
              </option>
              <option value="تجربی">تجربی</option>
              <option value="ریاضی">ریاضی</option>
            </select>
            <Error field="studentType" />
          </div>

          <div>
            <select
              name="studentSchool"
              aria-label="مدرسه"
              className={AUTH_FIELD}
              defaultValue=""
              {...register("studentSchool", {
                required: "لطفا مدرسه خود را وارد کنید",
              })}
              onChange={(e) =>
                setCall({ ...call, student_school: e.target.value })
              }
            >
              <option value="" disabled>
                مدرسه
              </option>
              <option value={1}>شهید اژه‌ای</option>
              <option value={2}>امام صادق(ع)</option>
              <option value={3}>سایر</option>
            </select>
            <Error field="studentSchool" />
          </div>

          <div>
            <select
              name="studentGender"
              aria-label="جنسیت"
              className={AUTH_FIELD}
              defaultValue=""
              {...register("studentGender", {
                required: "لطفا جنسیت خود را وارد کنید",
              })}
              onChange={(e) =>
                setCall({ ...call, student_gender: e.target.value })
              }
            >
              <option value="" disabled>
                جنسیت
              </option>
              <option value="پسر">پسر</option>
              <option value="دختر">دختر</option>
            </select>
            <Error field="studentGender" />
          </div>

          <div>
            <select
              name="studentGrade"
              aria-label="پایه‌ی تحصیلی"
              className={AUTH_FIELD}
              defaultValue=""
              {...register("studentGrade", {
                required: "لطفا پایه‌ی خود را وارد کنید",
              })}
              onChange={(e) =>
                setCall({ ...call, student_grade: e.target.value })
              }
            >
              <option value="" disabled>
                پایه‌ی تحصیلی
              </option>
              <option value="دهم">دهم</option>
              <option value="یازدهم">یازدهم</option>
              <option value="دوازدهم">دوازدهم</option>
            </select>
            <Error field="studentGrade" />
          </div>
        </div>

        <button
          type="submit"
          disabled={registerFetch.loading}
          className="mt-md inline-flex w-full items-center justify-center gap-sm rounded-pill bg-primary px-2xl py-md text-md font-medium text-white transition-colors duration-150 hover:bg-primary-hover active:bg-primary-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-60 motion-reduce:transition-none"
        >
          {registerFetch.loading && (
            <CircularProgress size="2rem" color="inherit" />
          )}
          ثبت نام
        </button>
      </form>
    </AuthPanel>
  );
};

export default Register;
